import React from 'react';
import styled, { keyframes } from 'styled-components';

import { NewArrowRight } from '@crehana/icons';

import { PrimaryButton, Loader } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import { createUrlWithSourceQueryParams } from 'Jsx/Analytics/Amplitude';
import { SHOPPING_CART_AMPLITUDE_PAGE_NAME } from 'Jsx/ShoppingCart/constants';
import { SHOPPING_CART_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';
import PaymentWithSavedCard from 'Jsx/CheckoutV4/shared/PaymentWithSavedCard';
import useSavedCards from 'Jsx/Shared/Hooks/useSavedCards';

import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';

const placeHolderShimmer = keyframes`
  from {
    background-position: 100% 0
  }
  to {
    background-position: -100% 0
  }
`;

export const Skeleton = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  animation-duration: 1.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: ${placeHolderShimmer};
  animation-timing-function: linear;
  background: #f6f7f8;
  background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
  background-size: 800px 104px;
`;

const Root = styled.div`
  top: 24px;
  @media (min-width: 1024px) {
    margin-top: -44px;
  }
`;

type SummaryProps = {
  loadingCart: boolean;
  cartSummary: any;
};

const Summary = ({ cartSummary, loadingCart }: SummaryProps) => {
  const { t } = useTranslation(SHOPPING_CART_TRANSLATION_KEY);

  const { hasSavedCard, loading: loadingSavedCard } = useSavedCards();

  const {
    totalPrice: price,
    totalRealPrice: realPrice,
    currencySymbol,
    totalOff: totalDiscount,
    idCart,
    totalPriceWithCredits,
    credit,
  } = cartSummary;
  const save = realPrice - price;

  if (loadingSavedCard) {
    return (
      <div
        style={{ minHeight: '110px' }}
        className="flex items-center justify-center w-full mt-32"
      >
        <Loader />
      </div>
    );
  }
  const checkoutUrl = createUrlWithSourceQueryParams({
    url: `/payment/?shopping_cart_id=${idCart}`,
    source: SHOPPING_CART_AMPLITUDE_PAGE_NAME,
    sourceDetail: 'Summary',
  });
  const catalogUrl = createUrlWithSourceQueryParams({
    url: '/cursos-online/',
    source: SHOPPING_CART_AMPLITUDE_PAGE_NAME,
    sourceDetail: 'Summary',
  });

  return (
    <Root>
      <div
        className="pt-32 pb-40 pl-40 pr-40 flex-grow-1"
        style={{ backgroundColor: '#f6f7f9', borderRadius: 4 }}
      >
        <div className="text-base-main">
          <p className="mb-16 font-h4">
            {t('SUMMARY', { defaultValue: 'Resumen' })}
          </p>
          {save > 0 && totalDiscount > 0 && (
            <div>
              {/* Total Price */}
              <p className="flex items-center justify-between pb-8 border-b border-solid border-gray-main">
                <span className="font-body4">{t('TOTAL_IN_CART')}</span>
                <span className="font-price-xs">
                  {currencySymbol}
                  {realPrice.toLocaleString()}
                </span>
              </p>
              {/* Discounts */}
              <p className="flex items-center justify-between pt-16 pb-8 border-b border-solid border-gray-main">
                <span className="font-body4" style={{ width: '50%' }}>
                  {t('APPLIED_DISCOUNTS')}
                </span>
                <span className="font-price-xs">
                  - {currencySymbol}
                  {save.toLocaleString()}
                </span>
              </p>
              {!!credit && (
                <p className="flex items-center justify-between pt-16 pb-8 border-b border-solid border-gray-main">
                  <span className="font-body4" style={{ width: '40%' }}>
                    {t('APPLIED_CREDITS')}
                  </span>
                  <span className="font-price-xs">
                    - {currencySymbol} {credit.toLocaleString()}
                  </span>
                </p>
              )}
            </div>
          )}
          <div className="pt-24">
            {/* Total Price */}
            <p className="flex items-center justify-between mb-8">
              <span className="font-body4">{t('TOTAL')}</span>
              <span className="text-red-main font-price-s">
                {!loadingCart ? (
                  `${currencySymbol}
                    ${
                      totalPriceWithCredits &&
                      totalPriceWithCredits.toLocaleString()
                    }`
                ) : (
                  <Skeleton />
                )}
              </span>
            </p>
            {/* Buttons footer */}
            <div>
              {hasSavedCard ? (
                <PaymentWithSavedCard
                  itemId={idCart}
                  itemType="shopping_cart"
                  urlToBuyNow={`/payment/?shopping_cart_id=${cartSummary.idCart}`}
                  source="shopping_cart_cards"
                  paymentSource={PaymentSourceEnum.SHOPPING_CART_VIEW}
                  sourcePage={SHOPPING_CART_AMPLITUDE_PAGE_NAME}
                />
              ) : (
                <PrimaryButton
                  full
                  size="l"
                  href={checkoutUrl}
                  label={t('BUY_NOW')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <a
        href={catalogUrl}
        className="flex items-center justify-center mt-32 cursor-pointer"
      >
        <span className="mr-8 text-secondary-main font-link">
          Continuar comprando
        </span>
        <NewArrowRight
          className="fill-current text-secondary-main"
          size={20}
          ignoreColor
        />
      </a>
    </Root>
  );
};

export default Summary;
